import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import { Emitter } from 'vscode/vscode/vs/base/common/event';
import { Disposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { ExtensionIdentifier } from 'vscode/vscode/vs/platform/extensions/common/extensions';
import { Extensions } from 'vscode/vscode/vs/workbench/services/extensionManagement/common/extensionFeatures';
import { IExtensionFeaturesManagementService } from 'vscode/vscode/vs/workbench/services/extensionManagement/common/extensionFeatures.service';
import { Registry } from 'vscode/vscode/vs/platform/registry/common/platform';
import { localizeWithPath } from 'vscode/vscode/vs/nls';

var LanguageModelStatsService_1;
const _moduleId = "vs/workbench/contrib/chat/common/languageModelStats";
let LanguageModelStatsService = class LanguageModelStatsService extends Disposable {
    static { LanguageModelStatsService_1 = this; }
    static { this.MODEL_STATS_STORAGE_KEY_PREFIX = 'languageModelStats.'; }
    static { this.MODEL_ACCESS_STORAGE_KEY_PREFIX = 'languageModelAccess.'; }
    constructor(extensionFeaturesManagementService, _storageService) {
        super();
        this.extensionFeaturesManagementService = extensionFeaturesManagementService;
        this._storageService = _storageService;
        this._onDidChangeStats = this._register(( (new Emitter())));
        this.onDidChangeLanguageMoelStats = this._onDidChangeStats.event;
        this.sessionStats = ( (new Map()));
        this._register(_storageService.onDidChangeValue(-1 , undefined, this._store)(e => {
            const model = this.getModel(e.key);
            if (model) {
                this._onDidChangeStats.fire(model);
            }
        }));
    }
    hasAccessedModel(extensionId, model) {
        return this.getAccessExtensions(model).includes(extensionId.toLowerCase());
    }
    async update(model, extensionId, agent, tokenCount) {
        await this.extensionFeaturesManagementService.getAccess(extensionId, 'languageModels');
        this.addAccess(model, extensionId.value);
        let sessionStats = this.sessionStats.get(model);
        if (!sessionStats) {
            sessionStats = { extensions: [] };
            this.sessionStats.set(model, sessionStats);
        }
        this.add(sessionStats, extensionId.value, agent, tokenCount);
        this.write(model, extensionId.value, agent, tokenCount);
        this._onDidChangeStats.fire(model);
    }
    addAccess(model, extensionId) {
        extensionId = extensionId.toLowerCase();
        const extensions = this.getAccessExtensions(model);
        if (!extensions.includes(extensionId)) {
            extensions.push(extensionId);
            this._storageService.store(this.getAccessKey(model), JSON.stringify(extensions), -1 , 0 );
        }
    }
    getAccessExtensions(model) {
        const key = this.getAccessKey(model);
        const data = this._storageService.get(key, -1 );
        try {
            if (data) {
                const parsed = JSON.parse(data);
                if (Array.isArray(parsed)) {
                    return parsed;
                }
            }
        }
        catch (e) {
        }
        return [];
    }
    async write(model, extensionId, participant, tokenCount) {
        const modelStats = await this.read(model);
        this.add(modelStats, extensionId, participant, tokenCount);
        this._storageService.store(this.getKey(model), JSON.stringify(modelStats), -1 , 0 );
    }
    add(modelStats, extensionId, participant, tokenCount) {
        let extensionStats = modelStats.extensions.find(e => ExtensionIdentifier.equals(e.extensionId, extensionId));
        if (!extensionStats) {
            extensionStats = { extensionId, requestCount: 0, tokenCount: 0, participants: [] };
            modelStats.extensions.push(extensionStats);
        }
        if (participant) {
            let participantStats = extensionStats.participants.find(p => p.id === participant);
            if (!participantStats) {
                participantStats = { id: participant, requestCount: 0, tokenCount: 0 };
                extensionStats.participants.push(participantStats);
            }
            participantStats.requestCount++;
            participantStats.tokenCount += tokenCount ?? 0;
        }
        else {
            extensionStats.requestCount++;
            extensionStats.tokenCount += tokenCount ?? 0;
        }
    }
    async read(model) {
        try {
            const value = this._storageService.get(this.getKey(model), -1 );
            if (value) {
                return JSON.parse(value);
            }
        }
        catch (error) {
        }
        return { extensions: [] };
    }
    getModel(key) {
        if (key.startsWith(LanguageModelStatsService_1.MODEL_STATS_STORAGE_KEY_PREFIX)) {
            return key.substring(LanguageModelStatsService_1.MODEL_STATS_STORAGE_KEY_PREFIX.length);
        }
        return undefined;
    }
    getKey(model) {
        return `${LanguageModelStatsService_1.MODEL_STATS_STORAGE_KEY_PREFIX}${model}`;
    }
    getAccessKey(model) {
        return `${LanguageModelStatsService_1.MODEL_ACCESS_STORAGE_KEY_PREFIX}${model}`;
    }
};
LanguageModelStatsService = LanguageModelStatsService_1 = ( (__decorate([
    ( (__param(0, IExtensionFeaturesManagementService))),
    ( (__param(1, IStorageService)))
], LanguageModelStatsService)));
( (Registry.as(Extensions.ExtensionFeaturesRegistry))).registerExtensionFeature({
    id: 'languageModels',
    label: ( localizeWithPath(_moduleId, 0, "Language Models")),
    description: ( localizeWithPath(_moduleId, 1, "Language models usage statistics of this extension.")),
    access: {
        canToggle: false
    },
});

export { LanguageModelStatsService };
